import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Gated } from '@/components/permission-gate';
import { cn } from '@/lib/utils';

import type { LifecycleStep } from './types';

/**
 * The document-to-cash stepper, generic: green behind, blue now, grey ahead —
 * and a stage the document went past WITHOUT is grey and struck through, so
 * it never reads as done (bugs: Sales Order). The connector bar ahead of a
 * step is lit when that step is done or current.
 *
 * A step with `href` is a link to the document behind it. A step with an
 * `action` is the act that makes the missing document (D-09): a `to` opens
 * a pre-filled editor, an `onAct` is the page's own handler. Both are greyed
 * with the reason when the role cannot (`permission`, BR-PERM-17/-18), and
 * both carry the act as their accessible name — what the click will make.
 */
export function LifecycleStepper({ steps, label }: { steps: readonly LifecycleStep[]; label: string }) {
  const { t } = useTranslation();
  return (
    <ol className="flex items-center gap-0" aria-label={label}>
      {steps.map((step, index) => {
        const dot = (
          <span className="grid justify-items-center gap-1 text-center">
            <span
              data-step-state={step.state}
              className={cn(
                'flex size-8 items-center justify-center rounded-full border text-xs font-bold',
                step.state === 'done' && 'border-emerald-700 bg-emerald-700 text-white',
                step.state === 'current' && 'border-primary bg-primary text-primary-foreground',
                step.state === 'skipped' &&
                  'border-muted-foreground/40 bg-muted text-muted-foreground line-through',
                step.state === 'pending' && 'border-muted-foreground/30 text-muted-foreground',
              )}
            >
              {step.state === 'done' ? <Check className="size-4" aria-hidden /> : index + 1}
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t(step.labelKey)}
              {step.state === 'skipped' ? <span className="sr-only"> ({t('doclist.stepSkipped')})</span> : null}
            </span>
          </span>
        );
        const next = steps[index + 1];
        return (
          <li key={step.key} className="flex flex-1 items-center">
            <StepControl step={step}>{dot}</StepControl>
            {next !== undefined ? (
              <span
                aria-hidden
                className={cn(
                  'mx-1 mb-4 h-0.5 flex-1 rounded',
                  next.state === 'done' || next.state === 'current' ? 'bg-emerald-500' : 'bg-muted-foreground/20',
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function StepControl({ step, children }: { step: LifecycleStep; children: React.ReactElement }) {
  const { t } = useTranslation();
  if (step.href !== undefined) {
    return (
      <Link to={step.href} className="rounded-md hover:opacity-80">
        {children}
      </Link>
    );
  }
  const action = step.action;
  if (action === undefined) return children;
  const name = t(action.labelKey);
  const control =
    action.to !== undefined ? (
      <Link to={action.to} title={name} aria-label={name} className="rounded-md hover:opacity-80">
        {children}
      </Link>
    ) : (
      <button
        type="button"
        onClick={action.onAct}
        disabled={action.disabled === true}
        title={name}
        aria-label={name}
        className="rounded-md hover:opacity-80 disabled:opacity-60"
      >
        {children}
      </button>
    );
  if (step.permission === undefined) return control;
  return (
    <Gated permission={step.permission.permission} reasonKey={step.permission.reasonKey}>
      {control}
    </Gated>
  );
}
